import { useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';

export interface HeaderMenuItem {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  /** When true, the item is rendered in danger color and not selectable
   *  if `disabled` is set. */
  danger?: boolean;
  disabled?: boolean;
}

/** Header "..." dropdown menu for detail pages.
 *  - Closes on outside click and on Escape.
 *  - Disabled items render greyed out and ignore clicks. */
export function HeaderActionMenu({
  items,
  ariaLabel,
}: {
  items: HeaderMenuItem[];
  ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block" data-no-print="true">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-9 h-9 rounded-lg flex items-center justify-center"
        style={{
          border: '1px solid var(--pro-border)',
          color: 'var(--pro-text-dark)',
          background: open ? 'var(--pro-grey-subtle)' : 'white',
        }}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-1 w-56 rounded-lg z-20 pro-scale-in py-1"
          style={{
            background: 'white',
            border: '1px solid var(--pro-border)',
            boxShadow: '0 8px 24px rgba(13,13,18,0.08)',
          }}
        >
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <button
                key={it.key}
                type="button"
                role="menuitem"
                onClick={() => {
                  if (it.disabled) return;
                  setOpen(false);
                  it.onClick();
                }}
                disabled={it.disabled}
                className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  color: it.danger ? 'var(--pro-danger)' : 'var(--pro-text-dark)',
                  fontFamily: 'Figtree',
                  background: 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (!it.disabled) {
                    e.currentTarget.style.background = it.danger
                      ? 'rgba(223,28,65,0.06)'
                      : 'var(--pro-grey-subtle)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <Icon className="w-4 h-4" />
                {it.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
